/**
 * Modern Error/Success Notification Component
 * Clean, minimalistic toast-style notifications with theme support
 */

import { useEffect } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import './ErrorNotification.css';

interface NotificationProps {
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  onClose: () => void;
}

function Notification({ title, message, type, onClose }: NotificationProps) {
  useEffect(() => {
    // Auto-close info notifications after 5 seconds for undo actions
    if (type === 'info') {
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [type, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return '✅';
      default:
        return 'ℹ️';
    }
  };

  const handleUndo = (e: React.MouseEvent) => {
    e.stopPropagation();
    // For delete notifications, trigger undo
    if (title === 'Content Cleared') {
      const deleted = (window as any)._lastDeletedContent;
      if (deleted && Date.now() - deleted.timestamp < 10000) {
        // Trigger the global undo delete handler
        window.dispatchEvent(new CustomEvent('undoDelete'));
      }
    }
    onClose();
  };

  const isDeleteNotification = title === 'Content Cleared';

  return (
    <div className={`notification notification-${type}`} role="alert" aria-live="polite">
      <div className="notification-icon">{getIcon()}</div>
      <div className="notification-content">
        <div className="notification-title">{title}</div>
        {message && <div className="notification-message">{message}</div>}
        {isDeleteNotification && (
          <div className="notification-shortcut">
            <em>Ctrl+Z</em>
          </div>
        )}
      </div>
      {isDeleteNotification && (
        <button
          type="button"
          className="notification-undo"
          onClick={handleUndo}
          aria-label="Undo delete"
        >
          ↶ Undo
        </button>
      )}
      <button
        type="button"
        className="notification-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  );
}

export function ErrorNotification() {
  const { errorNotification, clearError } = useAuthStore();

  if (!errorNotification) return null;

  const handleClose = () => {
    clearError();
  };

  return (
    <div className="notification-container">
      <Notification
        title={errorNotification.title}
        message={errorNotification.message}
        type={errorNotification.type}
        onClose={handleClose}
      />
    </div>
  );
}
